import { useEffect, useRef } from "react";
import { cn } from "~/lib/utils";

interface TrustpilotWidgetProps {
  templateId: string;
  // Fixed-height TrustBoxes (Carousel/Slider/Micro) set `height`; the Flex TrustBox
  // sizes itself via `styleSize` (data-style-size, e.g. "S") instead — pass one or the other.
  height?: string;
  styleSize?: string;
  width?: string;
  token: string;
  stars?: string;
  reviewLanguages?: string;
  // Flex TrustBox theming / review-snippet options (ignored by templates that don't use them).
  textColor?: string;
  background?: string;
  externalElementsColor?: string;
  useReviewSnippets?: boolean;
  reviewSnippetsLimit?: number;
  reviewSnippetsTopic?: string;
  reviewSnippetsQuotes?: boolean;
  className?: string;
}

const BUSINESS_UNIT_ID = "60af72b734523f0001ff8983";

export default function TrustpilotWidget({
  templateId,
  height,
  styleSize,
  width = "100%",
  token,
  stars,
  reviewLanguages,
  textColor,
  background,
  externalElementsColor,
  useReviewSnippets,
  reviewSnippetsLimit,
  reviewSnippetsTopic,
  reviewSnippetsQuotes,
  className,
}: TrustpilotWidgetProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // If window.Trustpilot is available it means that we need to load the TrustBox from our ref.
    // If it's not, it means the script you pasted into <head /> isn't loaded  just yet.
    // When it is, it will automatically load the TrustBox.
    if (window.Trustpilot) {
      window.Trustpilot.loadFromElement(ref.current, true);
    }
  }, []);

  return (
    <div
      ref={ref}
      className={cn("trustpilot-widget", className)}
      data-locale="en-US"
      data-template-id={templateId}
      data-businessunit-id={BUSINESS_UNIT_ID}
      data-style-width={width}
      data-token={token}
      {...(height && { "data-style-height": height })}
      {...(styleSize && { "data-style-size": styleSize })}
      {...(stars && { "data-stars": stars })}
      {...(reviewLanguages && { "data-review-languages": reviewLanguages })}
      {...(textColor && { "data-text-color": textColor })}
      {...(background && { "data-background": background })}
      {...(externalElementsColor && { "data-external-elements-color": externalElementsColor })}
      {...(useReviewSnippets !== undefined && { "data-use-review-snippets": String(useReviewSnippets) })}
      {...(reviewSnippetsLimit !== undefined && { "data-review-snippets-limit": String(reviewSnippetsLimit) })}
      {...(reviewSnippetsTopic && { "data-review-snippets-topic": reviewSnippetsTopic })}
      {...(reviewSnippetsQuotes !== undefined && { "data-review-snippets-quotes": String(reviewSnippetsQuotes) })}
    >
      <a href="https://www.trustpilot.com/review/blidz.com" target="_blank" rel="noopener">
        Trustpilot
      </a>
    </div>
  );
}
